import React, { useState, useMemo } from 'react';
import {
  TrendingUp, TrendingDown, Target, DollarSign,
  Award, Flame, Minus, Plus, ChevronDown
} from 'lucide-react';
import { tradeHistory } from '../data/tradeData';
import { formatCurrency } from '../utils/formatters';

const TOTAL_VERIFIED = tradeHistory.length;

const PERIODS = [
  { id: '1w', label: '1W' },
  { id: '1m', label: '1M' },
  { id: '3m', label: '3M' },
  { id: 'all', label: 'All' },
];

const TradesPage = () => {
  const [period, setPeriod] = useState('all');
  const [contracts, setContracts] = useState(1);
  const [showAllTrades, setShowAllTrades] = useState(false);

  const dateFiltered = useMemo(() => {
    if (period === 'all') return tradeHistory;
    const sorted = [...tradeHistory].sort((a, b) => b.date.localeCompare(a.date));
    const latest = sorted[0]?.date;
    if (!latest) return tradeHistory;
    const end = new Date(latest);
    const start = new Date(latest);
    if (period === '1w') start.setDate(end.getDate() - 7);
    else if (period === '1m') start.setMonth(end.getMonth() - 1);
    else if (period === '3m') start.setMonth(end.getMonth() - 3);
    return tradeHistory.filter(t => new Date(t.date) >= start && new Date(t.date) <= end);
  }, [period]);

  const stats = useMemo(() => {
    const wins = dateFiltered.filter(t => t.profit > 0);
    const losses = dateFiltered.filter(t => t.profit < 0);
    const totalPL = dateFiltered.reduce((s, t) => s + t.profit, 0);
    const avgWin = wins.length ? wins.reduce((s, t) => s + t.profit, 0) / wins.length : 0;
    const avgLoss = losses.length ? losses.reduce((s, t) => s + t.profit, 0) / losses.length : 0;
    const bestTrade = dateFiltered.length ? Math.max(...dateFiltered.map(t => t.profit)) : 0;
    const worstTrade = dateFiltered.length ? Math.min(...dateFiltered.map(t => t.profit)) : 0;
    const winRate = dateFiltered.length ? (wins.length / dateFiltered.length) * 100 : 0;
    return { totalPL, winRate, avgWin, avgLoss, bestTrade, worstTrade, total: dateFiltered.length };
  }, [dateFiltered]);

  const chartData = useMemo(() => {
    if (!dateFiltered.length) return { bars: [], labelType: 'day' };

    const dayMap = {};
    dateFiltered.forEach(t => { dayMap[t.date] = (dayMap[t.date] || 0) + t.profit; });
    const dailyEntries = Object.entries(dayMap).sort((a, b) => a[0].localeCompare(b[0]));

    if (period === '1w' || period === '1m') {
      return {
        bars: dailyEntries.map(([date, pl]) => ({ label: date, pl })),
        labelType: 'day',
      };
    }

    if (period === '3m') {
      const weekMap = {};
      dailyEntries.forEach(([date, pl]) => {
        const d = new Date(date + 'T00:00:00');
        const startOfWeek = new Date(d);
        startOfWeek.setDate(d.getDate() - d.getDay() + 1);
        const key = startOfWeek.toISOString().slice(0, 10);
        weekMap[key] = (weekMap[key] || 0) + pl;
      });
      return {
        bars: Object.entries(weekMap).sort((a, b) => a[0].localeCompare(b[0])).map(([date, pl]) => ({ label: date, pl })),
        labelType: 'week',
      };
    }

    const monthMap = {};
    dailyEntries.forEach(([date, pl]) => {
      const key = date.slice(0, 7);
      monthMap[key] = (monthMap[key] || 0) + pl;
    });
    return {
      bars: Object.entries(monthMap).sort((a, b) => a[0].localeCompare(b[0])).map(([date, pl]) => ({ label: date, pl })),
      labelType: 'month',
    };
  }, [dateFiltered, period]);

  const maxAbsPL = useMemo(() => {
    if (!chartData.bars.length) return 1;
    return Math.max(...chartData.bars.map(d => Math.abs(d.pl)), 1);
  }, [chartData]);

  const displayedTrades = showAllTrades ? dateFiltered : dateFiltered.slice(0, 20);

  const formatDate = (d) => {
    const date = new Date(d + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatChartLabel = (label, type) => {
    if (type === 'month') {
      const [y, m] = label.split('-');
      const date = new Date(Number(y), Number(m) - 1, 1);
      return date.toLocaleDateString('en-US', { month: 'short' });
    }
    if (type === 'week') {
      const date = new Date(label + 'T00:00:00');
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return formatDate(label);
  };

  const m = contracts;

  return (
    <div className="page">
      <div className="th">
        <div className="th-header">
          <div>
            <h1 className="th-header__title">Backtest Results</h1>
            <p className="th-header__sub">{TOTAL_VERIFIED.toLocaleString()} backtested trades</p>
          </div>
        </div>

        {/* Controls — one clean row */}
        <div className="th-controls">
          <div className="th-periods">
            {PERIODS.map(p => (
              <button
                key={p.id}
                className={`th-period ${period === p.id ? 'th-period--active' : ''}`}
                onClick={() => setPeriod(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="th-stepper">
            <span className="th-stepper__label">Contracts</span>
            <button
              className="th-stepper__btn"
              onClick={() => setContracts(c => Math.max(1, c - 1))}
              disabled={contracts <= 1}
            >
              <Minus size={14} />
            </button>
            <span className="th-stepper__value">{contracts}</span>
            <button
              className="th-stepper__btn"
              onClick={() => setContracts(c => Math.min(20, c + 1))}
              disabled={contracts >= 20}
            >
              <Plus size={14} />
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="th-stats">
          <div className="th-stat">
            <div className="th-stat__icon th-stat__icon--cyan"><DollarSign size={16} /></div>
            <div className="th-stat__body">
              <span className="th-stat__label">Total P&L</span>
              <span className={`th-stat__value ${stats.totalPL * m >= 0 ? 'th-stat__value--green' : 'th-stat__value--red'}`}>
                {stats.totalPL * m >= 0 ? '+' : ''}{formatCurrency(stats.totalPL * m)}
              </span>
            </div>
          </div>
          <div className="th-stat">
            <div className="th-stat__icon th-stat__icon--blue"><Target size={16} /></div>
            <div className="th-stat__body">
              <span className="th-stat__label">Win Rate</span>
              <span className="th-stat__value">{stats.winRate.toFixed(1)}%</span>
            </div>
          </div>
          <div className="th-stat">
            <div className="th-stat__icon th-stat__icon--green"><TrendingUp size={16} /></div>
            <div className="th-stat__body">
              <span className="th-stat__label">Avg Win</span>
              <span className="th-stat__value th-stat__value--green">+{formatCurrency(stats.avgWin * m)}</span>
            </div>
          </div>
          <div className="th-stat">
            <div className="th-stat__icon th-stat__icon--red"><TrendingDown size={16} /></div>
            <div className="th-stat__body">
              <span className="th-stat__label">Avg Loss</span>
              <span className="th-stat__value th-stat__value--red">{formatCurrency(stats.avgLoss * m)}</span>
            </div>
          </div>
          <div className="th-stat">
            <div className="th-stat__icon th-stat__icon--green"><Award size={16} /></div>
            <div className="th-stat__body">
              <span className="th-stat__label">Best Trade</span>
              <span className="th-stat__value th-stat__value--green">+{formatCurrency(stats.bestTrade * m)}</span>
            </div>
          </div>
          <div className="th-stat">
            <div className="th-stat__icon th-stat__icon--red"><Flame size={16} /></div>
            <div className="th-stat__body">
              <span className="th-stat__label">Worst Trade</span>
              <span className="th-stat__value th-stat__value--red">{formatCurrency(stats.worstTrade * m)}</span>
            </div>
          </div>
        </div>

        {/* P&L Chart */}
        <div className="th-chart">
          <div className="th-chart__header">
            <span className="th-chart__title">
              {chartData.labelType === 'month' ? 'Monthly' : chartData.labelType === 'week' ? 'Weekly' : 'Daily'} P&L
            </span>
            <span className="th-chart__hint">{contracts > 1 ? `${contracts} contracts` : '1 contract'}</span>
          </div>
          <div className="th-chart__bars">
            {chartData.bars.map(bar => {
              const pct = (Math.abs(bar.pl) / maxAbsPL) * 100;
              const isPositive = bar.pl >= 0;
              return (
                <div className="th-bar" key={bar.label} title={`${formatChartLabel(bar.label, chartData.labelType)}: ${bar.pl * m >= 0 ? '+' : ''}${formatCurrency(bar.pl * m)}`}>
                  <div className="th-bar__track">
                    <div
                      className={`th-bar__fill ${isPositive ? 'th-bar__fill--green' : 'th-bar__fill--red'}`}
                      style={{ height: `${Math.max(pct, 4)}%` }}
                    />
                  </div>
                  <span className="th-bar__date">{formatChartLabel(bar.label, chartData.labelType)}</span>
                  <span className={`th-bar__val ${isPositive ? 'th-bar__val--green' : 'th-bar__val--red'}`}>
                    {isPositive ? '+' : ''}{formatCurrency(bar.pl * m)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Table */}
        <div className="th-table-wrap">
          <div className="th-table__header">
            <span className="th-table__title">Trades</span>
            <span className="th-table__count">{dateFiltered.length} of {TOTAL_VERIFIED.toLocaleString()}</span>
          </div>
          <table className="th-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Entry</th>
                <th>Exit</th>
                <th>P&L</th>
                {contracts > 1 && <th>P&L ({contracts} ct)</th>}
              </tr>
            </thead>
            <tbody>
              {displayedTrades.map(trade => (
                <tr key={trade.id}>
                  <td>{formatDate(trade.date)}</td>
                  <td className="th-table__time">{trade.time}</td>
                  <td>{formatCurrency(trade.entryPrice, true)}</td>
                  <td>{formatCurrency(trade.exitPrice, true)}</td>
                  <td>
                    <span className={`th-pl ${trade.profit >= 0 ? 'th-pl--green' : 'th-pl--red'}`}>
                      {trade.profit >= 0 ? '+' : ''}{formatCurrency(trade.profit, true)}
                    </span>
                  </td>
                  {contracts > 1 && (
                    <td>
                      <span className={`th-pl th-pl--bold ${trade.profit >= 0 ? 'th-pl--green' : 'th-pl--red'}`}>
                        {trade.profit * m >= 0 ? '+' : ''}{formatCurrency(trade.profit * m, true)}
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {dateFiltered.length > 20 && (
            <div className="th-table__footer">
              <span className="th-table__showing">
                Showing {displayedTrades.length} of {dateFiltered.length}
              </span>
              <button className="th-table__more" onClick={() => setShowAllTrades(!showAllTrades)}>
                {showAllTrades ? 'Show Less' : 'Show All'}
                <ChevronDown size={14} style={{ transform: showAllTrades ? 'rotate(180deg)' : 'none' }} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TradesPage;
